import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import { useResource, PageTitle, Panel, Loading, ErrorState, Metric, DemoBadge, EmptyState, fmtMoney } from "../../components/app/Kit";
import { WeeklyReport } from "../../components/app/WeeklyReport";

const COLORS = ["#00FF66", "#00FFFF", "#0055FF", "#6B4C9A", "#A6AFBA", "#5cffa0"];
const tip = {
  contentStyle: {
    background: "#0A0F14", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 6, fontSize: 11, color: "#F7F9FC",
  },
};
const axis = { stroke: "#A6AFBA", fontSize: 10, tickLine: false, axisLine: false };

export default function Analytics() {
  const { data, state, error, reload } = useResource("/analytics/summary");

  if (state === "loading") return <Loading rows={8} testId="analytics-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="analytics-error" />;

  const m = data.metrics;

  return (
    <div data-testid="analytics-page">
      <PageTitle
        testId="analytics-title"
        title="Analytics"
        sub="See what actually drives revenue: pipeline, response time, AI resolution and conversion."
        action={<DemoBadge />}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric testId="analytics-leads" label="Leads" value={m.leads} />
        <Metric testId="analytics-appointments" label="Appointments" value={m.appointments} tone="cyan" />
        <Metric testId="analytics-conversion" label="Conversion rate" value={m.conversion_rate} suffix="%" />
        <Metric testId="analytics-calls" label="Calls" value={m.calls} tone="cyan" />
        <Metric testId="analytics-missed" label="Missed calls" value={m.missed_calls} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric testId="analytics-recovered" label="Recovered leads" value={m.recovered_leads} />
        <Metric testId="analytics-followups" label="Follow-ups run" value={m.follow_ups} tone="cyan" />
        <Metric testId="analytics-revenue" label="Revenue" value={m.revenue} hint="Paid demo invoices" />
        <Metric testId="analytics-response" label="Avg response time" value={m.avg_response_time_minutes} suffix=" min" tone="cyan" />
        <Metric testId="analytics-ai-rate" label="AI resolution rate" value={m.ai_resolution_rate} suffix="%" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel testId="analytics-leads-chart" title="Leads by date" action={<DemoBadge testId="analytics-demo-1" />}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series.leads}>
                <defs>
                  <linearGradient id="aLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00FF66" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} width={26} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="value" stroke="#00FF66" strokeWidth={2} fill="url(#aLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="analytics-appointments-chart" title="Appointments by date">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series.appointments}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} width={26} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#00FFFF" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="analytics-funnel" title="Conversion funnel">
          <div className="grid gap-2">
            {data.funnel.map((f, i) => {
              const max = Math.max(...data.funnel.map((x) => x.value), 1);
              return (
                <div key={f.stage} data-testid={`funnel-${f.stage}`}>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-elv-dim">{f.stage}</span>
                    <span className="text-elv-text">{f.value}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full rounded-full bg-white/[0.04]">
                    <div
                      className="h-2 rounded-full"
                      style={{ width: `${(f.value / max) * 100}%`, background: COLORS[i % COLORS.length] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel testId="analytics-revenue-chart" title="Revenue">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series.revenue}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} width={40} tickFormatter={(v) => `$${v}`} />
                <Tooltip {...tip} formatter={(v) => fmtMoney(v)} />
                <Line type="monotone" dataKey="value" stroke="#00FF66" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="analytics-sources" title="Lead sources">
          {data.sources.length === 0 ? (
            <EmptyState icon="PieChart" title="No source data" sub="Sources appear once leads are captured." testId="sources-empty" />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.sources} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} stroke="none">
                    {data.sources.map((s, i) => (
                      <Cell key={s.name} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...tip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {data.sources.map((s, i) => (
              <span key={s.name} className="flex items-center gap-1.5 text-[10px] text-elv-dim">
                <span className="h-2 w-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                {s.name} ({s.value})
              </span>
            ))}
          </div>
        </Panel>

        <Panel testId="analytics-ai-activity" title="Calls & AI activity">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ai_activity} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" {...axis} />
                <YAxis type="category" dataKey="name" {...axis} width={130} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#00FF66" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <WeeklyReport />

      <p className="mt-4 text-[11px] text-elv-dim">
        All figures on this page are derived from demonstration data in this workspace and do not represent real
        business results.
      </p>
    </div>
  );
}
